import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { moneyDisplay, qtyDisplay } from "@icraftx/shared/v1";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { ReturnRow } from "@/shared/api/returns";

export type PosTicketLineRow = {
  id: string;
  productId: string;
  description: string | null;
  qty: string;
  unitPrice: string;
  discountPct: string;
  lineNet: string;
  lineTax: string;
  lineGross: string;
  lotCode: string | null;
  serialCode: string | null;
  productName: string;
};

export type PosTenderRow = {
  id: string;
  amount: string;
  changeGiven: string;
  payMethodId: string;
  payMethodName: string;
  payMethodCode: string;
};

const lineHelper = createColumnHelper<DeskTableFeatures, PosTicketLineRow>();
const tenderHelper = createColumnHelper<DeskTableFeatures, PosTenderRow>();
const retHelper = createColumnHelper<DeskTableFeatures, ReturnRow>();

export function posTicketLineColumns() {
  return lineHelper.columns([
    lineHelper.accessor("productName", {
      id: "productName",
      header: "Product",
      size: 200,
    }),
    lineHelper.accessor("qty", {
      id: "qty",
      header: "Qty",
      size: 80,
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {qtyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("unitPrice", {
      id: "unitPrice",
      header: "Price",
      size: 96,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    lineHelper.accessor("lotCode", {
      id: "lotCode",
      header: "Lot",
      size: 100,
      cell: (info) => info.getValue() ?? "—",
    }),
    lineHelper.accessor("serialCode", {
      id: "serialCode",
      header: "Serial",
      size: 100,
      cell: (info) => info.getValue() ?? "—",
    }),
    lineHelper.accessor("lineGross", {
      id: "lineGross",
      header: "Gross",
      size: 100,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
  ]);
}

export function posTenderColumns() {
  return tenderHelper.columns([
    tenderHelper.accessor("payMethodName", {
      id: "payMethodName",
      header: "Method",
      size: 140,
    }),
    tenderHelper.accessor("amount", {
      id: "amount",
      header: "Amount",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    tenderHelper.accessor("changeGiven", {
      id: "changeGiven",
      header: "Change",
      size: 100,
      meta: { align: "right" },
      cell: (info) => {
        const v = info.getValue();
        return Number(v) > 0 ? (
          <span className="font-mono tabular-nums">{moneyDisplay(v)}</span>
        ) : (
          "—"
        );
      },
    }),
  ]);
}

export function posTicketReturnColumns(companySlug: string) {
  return retHelper.columns([
    retHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 140,
      cell: (info) => (
        <Link
          to={`/c/${companySlug}/returns/${info.row.original.id}`}
          className="font-medium font-mono text-sm no-underline"
        >
          {info.getValue()}
        </Link>
      ),
    }),
    retHelper.accessor("lifecycle", {
      id: "lifecycle",
      header: "Status",
      size: 88,
      cell: (info) => (
        <LifecycleStatus
          value={info.getValue()}
          to={`/c/${companySlug}/returns/${info.row.original.id}`}
        />
      ),
    }),
    retHelper.accessor("grossTotal", {
      id: "grossTotal",
      header: "Gross",
      size: 110,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    retHelper.accessor("postedAt", {
      id: "postedAt",
      header: "Posted",
      size: 160,
      cell: (info) => info.getValue() ?? "—",
    }),
  ]);
}
